<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import type { NavigationMenuItem } from "@nuxt/ui"
import { SbFab, SbFabAction, SbRouteTab, SbTabs } from "@unio/saltbox-ui"
import { ref, useTemplateRef } from "vue"

import AccountSwitcher from "@/components/AccountSwitcher.vue"
import AppFooter from "@/components/layout/AppFooter.vue"
import AppLayout from "@/components/layout/AppLayout.vue"
import AppPagePanel from "@/components/layout/AppPagePanel.vue"
import AppSidebar from "@/components/layout/AppSidebar.vue"
import ScannerModal from "@/components/ScannerModal.vue"
import SearchCommandPalette from "@/components/SearchCommandPalette.vue"
import UserMenu from "@/components/UserMenu.vue"
import { useNavbar } from "@/composables/useNavbar"
import { buildDefaultNavigationLinks } from "@/lib/defaultNavigation"
import { getJson } from "@/lib/fetch"
import { ICON } from "@/lib/icons"
import {
  rootPath,
  itemsPath,
  containersPath,
  capturePath,
  itemPath,
  captureBarcodePath,
} from "@/routes"

const sidebarOpen = ref(false)
const searchPalette = useTemplateRef<InstanceType<typeof SearchCommandPalette>>("searchPalette")

function openSearch() {
  searchPalette.value?.open()
}
const scannerOpen = ref(false)
const scannerStatus = ref<string | null>(null)
const { title: navbarTitle } = useNavbar()

function closeOnSelect() {
  sidebarOpen.value = false
}

const links: NavigationMenuItem[][] = buildDefaultNavigationLinks(closeOnSelect)

const searchLinks: NavigationMenuItem[][] = [
  [{ label: "Search", icon: ICON.search, onSelect: openSearch }],
]

let scanAbort: AbortController | null = null

async function handleScanToFind(code: string) {
  scanAbort?.abort()
  scanAbort = new AbortController()
  scannerStatus.value = "Looking up..."
  try {
    const res = await getJson(
      `${captureBarcodePath()}?barcode=${encodeURIComponent(code)}`,
      scanAbort.signal,
    )
    if (res.ok) {
      const data = await res.json()
      scannerOpen.value = false
      scannerStatus.value = null
      router.visit(itemPath({ id: data.id }))
    } else {
      scannerStatus.value = "Item not found"
      setTimeout(() => (scannerStatus.value = null), 2000)
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return
    scannerStatus.value = "Lookup failed"
    setTimeout(() => (scannerStatus.value = null), 2000)
  }
}

const mobileTab = ref("dashboard")
</script>

<template>
  <UApp>
    <AppLayout>
      <AppSidebar id="default" v-model:open="sidebarOpen" collapsible resizable>
        <template #header="{ collapsed }">
          <AccountSwitcher :collapsed="collapsed" class="min-w-0 flex-1" />
        </template>

        <template #default="{ collapsed }">
          <UNavigationMenu :collapsed="collapsed" :items="links" orientation="vertical" tooltip />

          <UNavigationMenu
            :collapsed="collapsed"
            :items="searchLinks"
            orientation="vertical"
            tooltip
            class="mt-auto"
          />
        </template>

        <template #footer="{ collapsed }">
          <UserMenu :collapsed="collapsed" />
        </template>
      </AppSidebar>

      <AppPagePanel>
        <template #header>
          <UDashboardNavbar
            :title="navbarTitle"
            :ui="{
              root: 'h-[calc(var(--ui-header-height)_+_var(--safe-top))] pt-[var(--safe-top)]',
            }"
          >
            <template #leading>
              <UDashboardSidebarCollapse />
            </template>
            <template #right>
              <UButton
                :icon="ICON.search"
                label="Search"
                color="neutral"
                variant="subtle"
                class="hidden lg:inline-flex"
                @click="openSearch"
              >
                <template #trailing>
                  <UKbd value="meta" size="sm" />
                  <UKbd value="K" size="sm" />
                </template>
              </UButton>
              <UButton
                :icon="ICON.search"
                color="neutral"
                variant="ghost"
                square
                class="lg:hidden"
                @click="openSearch"
              />
              <UColorModeButton color="neutral" variant="ghost" />
              <UTooltip text="Scan to find">
                <UButton
                  :icon="ICON.scanQr"
                  color="neutral"
                  variant="ghost"
                  square
                  @click="
                    () => {
                      scannerOpen = true
                    }
                  "
                />
              </UTooltip>
              <div id="navbar-actions" />
            </template>
          </UDashboardNavbar>
        </template>

        <template #body>
          <slot />
        </template>
      </AppPagePanel>

      <!-- Mobile FAB -->
      <SbFab
        :icon="ICON.capture"
        :active-icon="ICON.close"
        direction="up"
        square
        class="fixed right-4 bottom-[calc(var(--app-footer-height,5rem)+1rem)] z-50 lg:hidden"
      >
        <SbFabAction :icon="ICON.capture" label="Photo" :to="capturePath()" square />
        <SbFabAction :icon="ICON.scanQr" label="Scan QR" to="/capture?mode=scan" square />
        <SbFabAction :icon="ICON.manual" label="Manual" to="/capture?mode=manual" square />
      </SbFab>

      <!-- Mobile bottom nav -->
      <AppFooter bordered class="lg:hidden">
        <SbTabs v-model="mobileTab" stretch>
          <SbRouteTab name="dashboard" :icon="ICON.home" label="Home" :to="rootPath()" exact />
          <SbRouteTab name="items" :icon="ICON.item" label="Items" :to="itemsPath()" />
          <SbRouteTab
            name="containers"
            :icon="ICON.container"
            label="Containers"
            :to="containersPath()"
          />
          <SbRouteTab name="more" :icon="ICON.menu" label="More" to="/more" />
        </SbTabs>
      </AppFooter>
    </AppLayout>

    <SearchCommandPalette ref="searchPalette" />

    <ScannerModal
      v-model:open="scannerOpen"
      :status-override="scannerStatus"
      @scan="handleScanToFind"
    />
  </UApp>
</template>
